import { useCallback } from 'react';
import { Modal, Fade } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark, faArrowRight, faWandMagicSparkles, faCalendarCheck,
} from '@fortawesome/free-solid-svg-icons';
import InfoTooltip from './InfoTooltip';
import '../translations.js';
import './WhatsNewModal.css';

/**
 * WhatsNewModal — the "what's new" announcement shown once after the big agentic
 * upgrade (AI Studio skill builder, action-taking agents, app connections, more
 * channels).
 *
 * Styling is ported from the Wix-onboarding brand visual (WixAuthVisual / .wav-*):
 * a purple gradient panel with a faint grid + glow and a sliding logo marquee of
 * everything the agent now connects to. Each marquee logo carries the same hover
 * InfoTooltip the onboarding marquee uses.
 *
 * The top of the modal is a single bright promo card that jumps straight into the
 * AI Studio (/agent-studio/skills). The bottom CTA offers a personal walkthrough
 * via the existing Calendly live demo.
 *
 * The ONLY action that persists a dismissal is the "Do not show this again"
 * button: it closes the modal immediately, then saves to the DB via
 * /api/user_modal_preferences (modal_name = 'agentic_upgrade_modal'), the same
 * store MetaIntegrationModal and AppRatingModal use, so the modal never returns.
 * Every other way of closing (the X, the backdrop, the promo / Calendly CTAs)
 * just dismisses for this session and the modal can return on the next visit.
 *
 * Brand: #5616ea / DM Sans. No "chatbot", no em-dashes.
 */

// The AI Studio (conversation-first skill builder) route — where the promo jumps to.
const STUDIO_PATH = '/agent-studio/skills';

// The live demo Calendly already used on the Help page — a personal walkthrough of
// the portal, which is exactly what the bottom CTA offers.
const CALENDLY_URL = 'https://calendly.com/build-ultimo-bots/live-demo-ultimo-bots';

// Everything the agent can now connect to — real /public assets (apps + channels),
// the same logos the onboarding marquee uses. Brand names stay as literals.
const TOOLS = [
  { src: '/calendly-logo.png', name: 'Calendly' },
  { src: '/stripe-logo.png', name: 'Stripe' },
  { src: '/hubspot-logo.png', name: 'HubSpot' },
  { src: '/calcom-logo.png', name: 'Cal.com' },
  { src: '/mailchimp-logo.png', name: 'Mailchimp' },
  { src: '/zendesk-logo.png', name: 'Zendesk' },
  { src: '/zapier_logo.png', name: 'Zapier' },
  { src: '/notion-logo.png', name: 'Notion' },
  { src: '/google-drive.png', name: 'Google Drive' },
  { src: '/onedrive.png', name: 'OneDrive' },
  { src: '/wix-logo.png', name: 'Wix' },
  { src: '/webflow-logo.png', name: 'Webflow' },
  { src: '/wordpress-logo.png', name: 'WordPress' },
  { src: '/instagram-logo.webp', name: 'Instagram' },
  { src: '/facebook-logo.webp', name: 'Facebook' },
  { src: '/whatsapp-logo.webp', name: 'WhatsApp' },
];

export default function WhatsNewModal({ open, onClose, userId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const saveModalPreference = useCallback(async () => {
    if (!userId) return;
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}user_modal_preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, modal_name: 'agentic_upgrade_modal' }),
      });
    } catch (error) {
      console.error('Error saving modal preference:', error);
    }
  }, [userId]);

  // Plain dismiss (X / backdrop / CTAs): close for this session only, never persist.
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // "Do not show this again": the only action that persists. Close the modal first,
  // then save the dismissal to the DB so it never returns.
  const handleDoNotShowAgain = useCallback(async () => {
    onClose();
    await saveModalPreference();
  }, [onClose, saveModalPreference]);

  // Promo CTA: jump into the Studio. Closes for this session only (no persist).
  const handleOpenStudio = useCallback(() => {
    onClose();
    navigate(STUDIO_PATH);
  }, [onClose, navigate]);

  return (
    <Modal open={open} onClose={handleClose} closeAfterTransition>
      <Fade in={open} timeout={300}>
        <div className="wnm-panel" role="dialog" aria-label={t('wnm_title')}>
          <div className="wnm-grid" aria-hidden="true" />
          <div className="wnm-glow" aria-hidden="true" />

          <button type="button" className="wnm-close" onClick={handleClose} aria-label={t('close')}>
            <FontAwesomeIcon icon={faXmark} />
          </button>

          <div className="wnm-inner">
            {/* Header */}
            <span className="wnm-eyebrow">{t('wnm_eyebrow')}</span>
            <h2 className="wnm-title">{t('wnm_title')}</h2>
            <p className="wnm-sub">{t('wnm_sub')}</p>

            {/* Promo — the headline AI Studio, clickable straight into the builder */}
            <button type="button" className="wnm-promo" onClick={handleOpenStudio}>
              <span className="wnm-promo-badge">{t('wnm_promo_badge')}</span>
              <span className="wnm-promo-icon"><FontAwesomeIcon icon={faWandMagicSparkles} /></span>
              <span className="wnm-promo-text">
                <span className="wnm-promo-title">{t('wnm_promo_title')}</span>
                <span className="wnm-promo-desc">{t('wnm_promo_desc')}</span>
              </span>
              <span className="wnm-promo-cta">
                {t('wnm_promo_cta')}
                <FontAwesomeIcon icon={faArrowRight} />
              </span>
            </button>

            {/* Marquee — everything the agent now connects to (hover for a tooltip) */}
            <div className="wnm-more">
              <span className="wnm-more-label">{t('wnm_more')}</span>
              <div className="wnm-marquee">
                <div className="wnm-marquee-track">
                  {[...TOOLS, ...TOOLS].map((tool, i) => (
                    <InfoTooltip key={i} text={tool.name} placement="top" compact>
                      <span className="wnm-more-logo"><img src={tool.src} alt={tool.name} /></span>
                    </InfoTooltip>
                  ))}
                </div>
              </div>
            </div>

            {/* Calendly CTA — a personal walkthrough of the new features */}
            <div className="wnm-cta">
              <div className="wnm-cta-text">
                <span className="wnm-cta-title">{t('wnm_cta_title')}</span>
                <span className="wnm-cta-sub">{t('wnm_cta_text')}</span>
              </div>
              <button
                type="button"
                className="wnm-cta-btn"
                onClick={() => window.open(CALENDLY_URL, '_blank', 'noopener')}
              >
                <FontAwesomeIcon icon={faCalendarCheck} />
                {t('wnm_cta_button')}
              </button>
            </div>

            {/* Footer — the only persisting action: "do not show this again" */}
            <div className="wnm-footer">
              <button type="button" className="wnm-dnsa" onClick={handleDoNotShowAgain}>
                {t('do_not_show_again')}
              </button>
            </div>
          </div>
        </div>
      </Fade>
    </Modal>
  );
}
